/**
 * The base toolset connection (wayai-pro/wayai#5083, Track K): what WayAI reads from the data
 * product about a stored toolset-bound token, what it records on the connection, and the rules it
 * judges from that record.
 *
 * A toolset-bound token is opaque to WayAI. The one place it says what it reaches is its own
 * toolset resolve, `GET /v1/_by_token/toolsets/{slug}/resolved`, which answers the expanded
 * toolset, the base it serves (`base_id`), that base's environment and its current incarnation
 * (`base_generation`), and the token's own binding (`toolset_binding`). Every write that stores
 * a toolset connection's token resolves it there and records the result as the connection's
 * server-owned `binding` setting; every rule below is judged from that record, so the writes
 * that change which agents use the connection need no call to the data product.
 */

import { z } from 'zod';
import { isCustomerFacingAgentRole } from '../constants';
import {
  DATA_BASE_GENERATION_HEADER,
  REKOR_ACTOR_ADMIN_HEADER,
  REKOR_ACTOR_ID_HEADER,
  REKOR_ACTOR_LABEL_HEADER,
  REKOR_ACTOR_TYPE_HEADER,
  REKOR_ACCESS_SCOPE_CUSTOMER,
  REKOR_BOUND_ACCESS_HEADER,
  REKOR_BOUND_ACCESS_SCOPE_HEADER,
  REKOR_BOUND_BASE_HEADER,
  REKOR_BOUND_CUSTOMER_HEADER,
  REKOR_BOUND_PAGE_HEADER,
  REKOR_BOUND_PERSON_HEADER,
  REKOR_BOUND_TOKEN_HEADER,
  REKOR_BOUND_TOOLSET_HEADER,
  REKOR_MAX_ACCESS_HEADER_LENGTH,
  REKOR_MAX_ACTOR_ID_LENGTH,
  REKOR_MAX_ACTOR_LABEL_LENGTH,
  REKOR_MAX_PERSON_HEADER_LENGTH,
  REKOR_MAX_PERSON_ID_LENGTH,
  REKOR_MAX_PERSON_IDS,
  REKOR_MAX_PERSON_PARTIES,
  REKOR_PERSON_ALL,
  REKOR_PERSON_AUDIENCE_CUSTOMER,
  REKOR_PERSON_AUDIENCE_STAFF,
  REKOR_V1_PREFIX,
  rekorAttestation,
} from './rekor-attestation';
import { isEncodedAccessSet } from './setup/access-dimensions';

// =============================================
// Connection settings
// =============================================

/** The setting naming the toolset: its slug, as in its MCP URL (`…/t/{slug}/mcp`). */
export const TOOLSET_SLUG_SETTING = 'toolset';

/**
 * The SERVER-OWNED setting recording what the stored token resolved to when it was written. No
 * author ever states it: the connector's settings schema does not declare it, so the write path's
 * sanitizer strips any value a caller sends, and the connection-write hook writes it from the
 * resolve. Absent means the connection holds no token, and so reaches nothing.
 */
export const TOOLSET_BINDING_SETTING = 'binding';

/**
 * Settings keys the platform writes and no author states. The config-as-code diff ignores them on
 * both sides and pull never exports them, so a converged `hub.yaml` stays converged.
 */
export const SERVER_OWNED_CONNECTION_SETTINGS: readonly string[] = [TOOLSET_BINDING_SETTING];

/** A copy of `settings` without its server-owned keys — what an author states. */
export function withoutServerOwnedSettings(settings: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(settings).filter(([key]) => !SERVER_OWNED_CONNECTION_SETTINGS.includes(key)));
}

/**
 * The data product's charset and cap for a toolset slug, which it interpolates into its paths
 * (`STRUCTURAL_ID_RE` and `SLUG_ID_MAX_LENGTH` on the far side). A slug outside it names no
 * toolset, so it is refused by name before any call and never spliced into a URL. The same rule
 * holds a bound call's base id (`REKOR_STRUCTURAL_ID_PATTERN`).
 */
export const TOOLSET_SLUG_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

/** The far side's rule for an id it reads as a structural part of a name or path: a toolset's slug, a base's id. */
export const REKOR_STRUCTURAL_ID_PATTERN = TOOLSET_SLUG_PATTERN;

const baseEnvironmentSchema = z.enum(['preview', 'production']);

/** The operations a toolset's file tool publishes, one tool each (RK6). */
export const TOOLSET_FILE_OPERATIONS = ['list', 'get', 'put', 'delete', 'get_url', 'put_url'] as const;

export type ToolsetFileOperation = (typeof TOOLSET_FILE_OPERATIONS)[number];

/**
 * The file operations that mint a signed file URL (#5083 RK6 part 2): a download URL (`get_url`) and a
 * single-use upload URL (`put_url`), for code that moves a file's bytes itself. A toolset connection's
 * catalog leaves their tools out (`toolsetUrlToolNames`; owner decision 195, Q8 (A)): a native agent
 * copies a file into its sandbox instead.
 */
export const TOOLSET_FILE_URL_OPERATIONS = ['get_url', 'put_url'] as const satisfies readonly ToolsetFileOperation[];

/**
 * One of a toolset's file tools as the connection records it: its file type, the folder it reaches
 * (`''` for the whole file type; a folder boundary, so `public` reaches `public/eu/a.md` and never
 * `public-old/a.md`), its authored description, and the name each operation it publishes is
 * called under. The `data/` mount
 * of WayAI's file tools reaches a toolset's files through these tools (#5142).
 */
export const toolsetFileToolSchema = z.object({
  file_type: z.string().min(1),
  path_prefix: z.string(),
  /** The entry's authored description of its tools (`description_override`), when it has one. */
  description: z.string().min(1).optional(),
  tools: z.object({
    list: z.string().min(1).optional(),
    get: z.string().min(1).optional(),
    put: z.string().min(1).optional(),
    delete: z.string().min(1).optional(),
    get_url: z.string().min(1).optional(),
    put_url: z.string().min(1).optional(),
  }),
});

export type ToolsetFileTool = z.infer<typeof toolsetFileToolSchema>;

/**
 * What a toolset exposes, as the connection records it: the facts the customer-facing rule reads
 * (`toolsetBindingAllowsCustomerFacing`), whether its calls must name a customer (`customer_scoped`), and
 * the toolset's file tools.
 */
export const toolsetSurfaceSchema = z.object({
  /** Any record, composite, relationship or SQL Action tool: the toolset reaches records. */
  records: z.boolean(),
  sql_query: z.boolean(),
  batch: z.boolean(),
  /**
   * The toolset serves one customer per call (#5083 RK2): its every call names the customer it serves,
   * and reaches only that customer's records and shared ones. Only a customer page and an eval run name
   * one; an agent's other calls name none (#5473 2b1), so they are refused it. Absent on a surface recorded
   * before it was, which reads as not scoped until the connection's next tool sync or token save records
   * it: its calls name no customer, and the customer-facing rule admits none of its record tools to a
   * customer page.
   */
  customer_scoped: z.boolean().optional(),
  /**
   * A key of the resolve this side neither describes nor classifies exposes something: a kind of tool
   * the data product added later, counted in `records`. The customer-facing rule never admits it, to
   * any holder, until it is classified here. Absent on a surface recorded before it was,
   * which reads as present.
   */
  unclassified_tools: z.boolean().optional(),
  /**
   * Any of its file tools writes: an operation other than `list`, `get` and `get_url` (`put`, `delete`,
   * `put_url`, or one the data product adds later), or an entry this side cannot read. Absent on a surface recorded
   * before it was, which the customer-facing rule reads as writing until the connection's next tool
   * sync or token save records it.
   */
  file_writes: z.boolean().optional(),
  /**
   * A record type the resolve's `record_types` carries names the toolset among its writers
   * (`x-review.writers`, #5083 RK4): the toolset writes the type's drafts and reads its every record,
   * drafts included. Those are the types its actions and composite record steps reach, and every
   * reviewed type it reaches otherwise: at a relationship tool's or a composite relationship step's
   * end, in a `batch` entry, or, through `sql_query`, all of them (decision 198). Or such a type's
   * declaration this side cannot read, or a resolve reaching records that carries no record types.
   * Absent on a surface recorded before it was, which the customer-facing rule reads as writing, for a
   * toolset reaching records, until the connection's next tool sync or token save records it.
   */
  review_writes: z.boolean().optional(),
  /**
   * Every record type the toolset reaches, at least one, is reviewed (`x-review`), holds no customer data
   * (`shared`), and does not name it among its writers: each call reads only that type's approved
   * records (#5083 decision 182, Q9). Never with a SQL Action, whose query reads its types' every
   * record, drafts included. Absent on a surface recorded before it was, which reads as false.
   */
  approved_reads_only: z.boolean().optional(),
  /**
   * The toolset's file tools. Absent when it has none, and on a surface recorded before they were.
   * A list this side cannot read is dropped rather than failing the binding, which every call and
   * the customer-facing rule read: the next tool sync records it again.
   */
  files: z.array(toolsetFileToolSchema).optional().catch(undefined),
});

export type ToolsetSurface = z.infer<typeof toolsetSurfaceSchema>;

export const toolsetConnectionBindingSchema = z.object({
  base_id: z.string().min(1),
  /** The slug the token was resolved against — `settings.toolset` at that write. */
  toolset: z.string().min(1),
  /** The base's incarnation when the token was written; a bound attested call states it. */
  base_generation: z.number().int().min(1),
  environment: baseEnvironmentSchema,
  surface: toolsetSurfaceSchema,
  /**
   * The id of the token the binding was resolved from, as its resolve reports it; a bound attested
   * call names it (`REKOR_BOUND_TOKEN_HEADER`), so the data product refuses the binding once the token
   * is revoked or expires (owner decision 41). Absent on a binding recorded before the report carried
   * it: its calls name none until a token save, or a tool sync while the token still stands, records it.
   * One that does not read is dropped rather than failing the binding.
   */
  token_id: z.string().regex(REKOR_STRUCTURAL_ID_PATTERN).optional().catch(undefined),
});

export type ToolsetConnectionBinding = z.infer<typeof toolsetConnectionBindingSchema>;

/**
 * The binding a connection's settings record, or null when they record none, or one that does not
 * parse. Accepts the settings as stored (a JSON string) or decoded.
 */
export function readToolsetBinding(settings: unknown): ToolsetConnectionBinding | null {
  let decoded: unknown = settings;
  if (typeof settings === 'string') {
    try {
      decoded = JSON.parse(settings);
    } catch {
      return null;
    }
  }
  if (!decoded || typeof decoded !== 'object') return null;
  const parsed = toolsetConnectionBindingSchema.safeParse((decoded as Record<string, unknown>)[TOOLSET_BINDING_SETTING]);
  return parsed.success ? parsed.data : null;
}

// =============================================
// The resolve WayAI reads
// =============================================

/** The toolset resolve a stored token reaches, below the data service's origin. */
export function rekorToolsetResolvePath(slug: string): string {
  return `${REKOR_V1_PREFIX}/_by_token/toolsets/${encodeURIComponent(slug)}/resolved`;
}

/** A bound caller's toolset tool definitions (MCP `tools/list` shape), below the data service's origin. */
export function rekorToolsetToolsPath(slug: string): string {
  return `${REKOR_V1_PREFIX}/_by_token/toolsets/${encodeURIComponent(slug)}/tools`;
}

/**
 * The part of the tool definitions this side reads: each tool's name, description and input
 * schema, which a native agent is given. The rest is dropped. Every one is untrusted input: it is
 * sanitized and wrapped like any MCP server's before it reaches a model.
 */
export const rekorToolsetToolsSchema = z.object({
  data: z.object({
    tools: z.array(z.object({
      name: z.string(),
      description: z.string().optional(),
      inputSchema: z.record(z.unknown()).optional(),
    })),
  }),
});

export type RekorToolsetTool = z.infer<typeof rekorToolsetToolsSchema>['data']['tools'][number];

/**
 * A bound caller's one-shot tool call, below the data service's origin. The body is
 * `{ tool_name, arguments }`; a success answers the tool's result as `{ data }` (a list adds
 * `meta`), and a refusal the data service's error envelope `{ error: { code, message } }`.
 */
export function rekorToolsetCallPath(slug: string): string {
  return `${REKOR_V1_PREFIX}/_by_token/toolsets/${encodeURIComponent(slug)}/call`;
}

/**
 * A person id as the persons a bound call is for name it (wayai-pro/wayai#5473 §1): a person id or a contact
 * id, in the structural charset and within the far side's cap (`MAX_PERSON_ID_LEN`).
 */
export const rekorPersonIdSchema = z.string().max(REKOR_MAX_PERSON_ID_LENGTH).regex(REKOR_STRUCTURAL_ID_PATTERN);

/** Whom a bound call serves (#5473 §4): `staff` only when the turn table says so; any other call is customer-facing. */
export const rekorPersonAudienceSchema = z.enum([REKOR_PERSON_AUDIENCE_STAFF, REKOR_PERSON_AUDIENCE_CUSTOMER]);

/**
 * The persons a bound call is for (`REKOR_BOUND_PERSON_HEADER`, #5473 §9): its audience, every person-bound
 * party of the turn — each its person set's ids (empty when unproven), or `REKOR_PERSON_ALL` alone — and
 * whether the agent's own `"*"` applies, which it may only beside the `staff` audience (the far side refuses
 * it beside any other, a 401). No other key: the far side refuses one it does not know rather than ignore
 * what might narrow the call.
 */
export const rekorBoundPersonSchema = z.object({
  audience: rekorPersonAudienceSchema,
  parties: z.array(z.union([z.array(rekorPersonIdSchema).max(REKOR_MAX_PERSON_IDS), z.literal(REKOR_PERSON_ALL)]))
    .max(REKOR_MAX_PERSON_PARTIES),
  agent_all: z.boolean(),
}).strict().refine((person) => !person.agent_all || person.audience === REKOR_PERSON_AUDIENCE_STAFF, {
  message: 'The agent\'s "*" applies only to a staff call',
  path: ['agent_all'],
});

export type RekorBoundPerson = z.infer<typeof rekorBoundPersonSchema>;

/**
 * Whether the persons a bound call is for have full reach (#5473 §4, defined positively): at least one
 * person-bound party, every one holding `REKOR_PERSON_ALL`; or no party and the agent's own `"*"`, which WayAI
 * attests only on a no-party turn. Nothing else has it. The audience is not part of it.
 */
export function boundPersonHasFullReach(person: RekorBoundPerson): boolean {
  if (person.parties.length === 0) return person.agent_all;
  return person.parties.every((party) => party === REKOR_PERSON_ALL);
}

/** The header text of the persons a bound call is for: compact JSON, the form the header's caps are stated over. */
export function encodeBoundPerson(person: RekorBoundPerson): string {
  return JSON.stringify(person);
}

/** Whether `raw` is the header text of the persons a bound call is for. */
export function isEncodedBoundPerson(raw: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return false;
  }
  return rekorBoundPersonSchema.safeParse(parsed).success;
}

/**
 * The attestation of a call bound to one toolset (#5083 RK3). WayAI attests these for a hub's
 * toolset connection: the actor is a `token` (the agent calling, or the connection reading its own tool
 * definitions), or, on a team page's call only, the page's signed-in viewer as a `user` (wayai#5333
 * decision 8, Phase 2: beside the page's hub, naming no customer, the refinement below). The far side
 * relays that person as a `user` actor yet never as interactive, nor as the person writer: a bound call
 * stays agent-like there (`isInteractiveAuth`, `isPersonWriter`). Platform admin is always `0` (the far side refuses
 * a bound call that carries it), and the binding and incarnation are the ones the connection
 * recorded when its token was written. Every shape rule the far side's attested-auth middleware
 * refuses a bound attestation for is stated here (`apps/rekor/backend/src/middleware/attested-auth.ts`),
 * so a header bag that parses passes every shape check there. Two refusals there are not shapes and
 * cannot be stated here: a bag naming its token (`X-Rekor-Bound-Token`) is refused with a 401 when
 * that token no longer stands — revoked, expired, bound to another base or toolset, or with no
 * readable record (owner decision 41) — and with a 403 when that token is bound to persons (#5473
 * 2-tokens), which serves only its own calls. What the call then reaches is the far side's to judge (an
 * absent base, a stale incarnation, a tool the toolset does not publish).
 */
export const rekorBoundAttestation = rekorAttestation.extend({
  [REKOR_ACTOR_TYPE_HEADER]: z.enum(['token', 'user']),
  /** Trimmed and checked as the far side trims and checks it. */
  [REKOR_ACTOR_ID_HEADER]: z.string().trim().min(1).max(REKOR_MAX_ACTOR_ID_LENGTH),
  [REKOR_ACTOR_LABEL_HEADER]: z.string().max(REKOR_MAX_ACTOR_LABEL_LENGTH),
  [REKOR_ACTOR_ADMIN_HEADER]: z.literal('0'),
  [REKOR_BOUND_BASE_HEADER]: z.string().regex(REKOR_STRUCTURAL_ID_PATTERN),
  [REKOR_BOUND_TOOLSET_HEADER]: z.string().regex(REKOR_STRUCTURAL_ID_PATTERN),
  /**
   * Decimal digits only, as the far side parses it, and at most 15 of them: its `Number()` reads
   * every such value back exactly, where a longer one may round or overflow to `Infinity` (a 400).
   */
  [DATA_BASE_GENERATION_HEADER]: z.string().regex(/^[1-9]\d{0,14}$/),
  /** The token the binding was resolved from, when the connection records it (owner decision 41). */
  [REKOR_BOUND_TOKEN_HEADER]: z.string().regex(REKOR_STRUCTURAL_ID_PATTERN).optional(),
  /**
   * The customer the call serves (#5083 RK2): a contact id, which the contact book mints as a UUID.
   * Every UUID is an id the far side reads back as the customer's external id.
   */
  [REKOR_BOUND_CUSTOMER_HEADER]: z.string().uuid().optional(),
  /**
   * The access set the call carries (#5358): an access set's header text (`encodeAccessSet`), within
   * the far side's header cap. Tool calls carry one; the config reads (resolve, tool definitions) do not.
   */
  [REKOR_BOUND_ACCESS_HEADER]: z.string().max(REKOR_MAX_ACCESS_HEADER_LENGTH).refine(isEncodedAccessSet, {
    message: 'Not an access set',
  }).optional(),
  /**
   * Where the access set applies (#5358), when only on the call's customer's own records: its one value.
   * Sent only beside the set, which the far side holds it to (the refinement below).
   */
  [REKOR_BOUND_ACCESS_SCOPE_HEADER]: z.literal(REKOR_ACCESS_SCOPE_CUSTOMER).optional(),
  /**
   * The hub whose page makes the call (#5333): sent on every call of the page bridge, and on no other.
   * A hub id, read by the far side under the same structural charset as the other bound ids.
   */
  [REKOR_BOUND_PAGE_HEADER]: z.string().regex(REKOR_STRUCTURAL_ID_PATTERN).optional(),
  /**
   * The persons the call is for (#5473 §9): their header text (`encodeBoundPerson`), within the far side's
   * header cap. Every tool call carries it beside its access set (`apps/backend/src/rekor/access-set.ts`); the
   * config reads do not. The far side holds a call's reads and writes of a person-ruled record type to its parties,
   * whatever its audience, and a customer-facing call to its audience gate (#5473 2a).
   */
  [REKOR_BOUND_PERSON_HEADER]: z.string().max(REKOR_MAX_PERSON_HEADER_LENGTH).refine(isEncodedBoundPerson, {
    message: 'Not the persons a bound call is for',
  }).optional(),
}).superRefine((bag, ctx) => {
  if (bag[REKOR_BOUND_ACCESS_SCOPE_HEADER] !== undefined && bag[REKOR_BOUND_ACCESS_HEADER] === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [REKOR_BOUND_ACCESS_SCOPE_HEADER], message: 'Accepted only beside an access set' });
  }
  // A person rides only a team page's call: an agent's call and a connection's read name no page, and a
  // customer page's call, which names its customer, is the page's (wayai#5333 decision 8).
  if (bag[REKOR_ACTOR_TYPE_HEADER] === 'user'
    && (bag[REKOR_BOUND_PAGE_HEADER] === undefined || bag[REKOR_BOUND_CUSTOMER_HEADER] !== undefined)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [REKOR_ACTOR_TYPE_HEADER], message: 'A person is attested only on a team page\'s call' });
  }
});

export type RekorBoundAttestation = z.infer<typeof rekorBoundAttestation>;

/**
 * The data product's error code for a base whose id was destroyed and claimed again since the
 * caller's incarnation (`BASE_INCARNATION_MISMATCH_CODE` on the far side): a 409.
 */
export const REKOR_BASE_INCARNATION_MISMATCH = 'BASE_INCARNATION_MISMATCH';

/**
 * The data product's error code for a tool call naming a tool its toolset does not publish
 * (`TOOL_NOT_FOUND_CODE` on the far side).
 */
export const REKOR_TOOL_NOT_FOUND = 'TOOL_NOT_FOUND';

/**
 * The data product's error code for a call to a customer-scoped toolset that names no customer
 * (`CUSTOMER_SCOPE_REQUIRED_CODE` on the far side): a 403. A toolset connection's call names one only
 * when the connection records its toolset as customer-scoped and the conversation has a customer.
 */
export const REKOR_CUSTOMER_SCOPE_REQUIRED = 'CUSTOMER_SCOPE_REQUIRED';

/**
 * The data product's error code for a call naming a customer to a toolset that is not customer-scoped
 * (`CUSTOMER_SCOPE_MISMATCH_CODE` on the far side): a 403. A toolset connection's call meets it when
 * the toolset's scope was turned off since the connection recorded it.
 */
export const REKOR_CUSTOMER_SCOPE_MISMATCH = 'CUSTOMER_SCOPE_MISMATCH';

/**
 * The part of the toolset resolve this side reads. Deliberately not `.strict()`, and passing
 * unknown keys through: the producer is the data product, on its own deploy cadence, and the tool
 * arrays are its own shapes — only their presence is read here, and a key this side does not know
 * is judged by `summarizeToolsetSurface` rather than dropped.
 */
export const rekorResolvedToolsetSchema = z.object({
  data: z.object({
    base_id: z.string().min(1),
    environment: baseEnvironmentSchema,
    base_generation: z.number().int().min(1),
    /**
     * The calling token's binding, or null for a token bound to none. A bound token can call only
     * its toolset's tools, which is what a toolset connection stores (#5083 decision 14). Its org
     * is the token's own: base ids are unique only per org, so the base id alone does not say
     * which base the token reaches.
     */
    toolset_binding: z.object({
      org_id: z.string().optional(),
      base_id: z.string(),
      toolset_id: z.string(),
      /**
       * The token's own id, which a bound call names (`REKOR_BOUND_TOKEN_HEADER`); absent when the
       * caller has none (owner decision 41), and dropped when it is not an id the far side reads back.
       */
      token_id: z.string().regex(REKOR_STRUCTURAL_ID_PATTERN).optional().catch(undefined),
    }).nullable().optional(),
    actions: z.array(z.unknown()).optional(),
    composites: z.array(z.unknown()).optional(),
    relationships: z.array(z.unknown()).optional(),
    /**
     * The toolset's SQL Actions (#5311): each a fixed, read-only query over the record types it
     * declares, which the resolve also carries in `record_types` and `record_type_scopes`.
     */
    sql_actions: z.array(z.unknown()).optional(),
    files: z.array(z.unknown()).optional(),
    batch: z.object({ enabled: z.boolean().optional() }).passthrough().nullable().optional(),
    sql_query: z.boolean().optional(),
    /** The toolset serves one customer per call (#5083 RK2). Absent reads as not scoped. */
    customer_scope: z.boolean().optional(),
    /**
     * Record types the toolset reaches, by id, as the data product stores them (`json_schema`
     * included): each one its actions, composite record steps and SQL Actions reach, and each reviewed one it
     * reaches otherwise (relationship ends, `batch` entries, every one through `sql_query`; #5083
     * decision 198). An unreviewed type it reaches only otherwise is not here. A reviewed type's
     * `x-review` names its writers (RK4).
     */
    record_types: z.record(z.unknown()).optional(),
    /**
     * What each record type the toolset's actions, composite steps, SQL Actions and relationship tools reach is to a
     * customer scope (`{ role }`, or null), by id (#5083 RK2). A reviewed type `record_types` carries
     * because only a `batch` entry or `sql_query` reaches it has none.
     */
    record_type_scopes: z.record(z.unknown()).optional(),
    /**
     * The kind of each tool the toolset publishes, by the name it is published under (wayai#5333 decision
     * 6), sent only to a caller that asks (`REKOR_TOOL_KINDS_RESOLVE_SEARCH`). Read by `rekorToolKindLookup`;
     * a map this side cannot read is dropped, so every tool reads as a write.
     */
    tool_kinds: z.record(z.string()).optional().catch(undefined),
  }).passthrough(),
});

export type RekorResolvedToolset = z.infer<typeof rekorResolvedToolsetSchema>['data'];

/**
 * The resolve's keys that are not tools: the toolset's own description, the record types it
 * reaches, expanded (`record_types`: those its actions, composite record steps and SQL Actions reach, and every
 * reviewed one it reaches otherwise), and what the data product reports about the base and the
 * caller. With the tool keys `summarizeToolsetSurface` classifies, these are every key the resolve
 * carries today.
 *
 * `customer_scope` and `record_type_scopes` (RK2) describe whom the toolset's record tools serve,
 * and expose no tool of their own; nor does `tool_kinds` (wayai#5333 decision 6), which says what each
 * tool the other keys expose does, and is judged by its reader (`rekorToolKindLookup`), never here. The surface records `customer_scope` (`customer_scoped`), which
 * the customer-facing rule reads for a customer page (#5083 decision 23), and, from `record_types` and
 * `record_type_scopes`, whether the toolset writes or only reads reviewed record types (RK4).
 */
const RESOLVE_DESCRIPTIVE_KEYS: ReadonlySet<string> = new Set([
  'id', 'name', 'description', 'created_at', 'updated_at', 'record_types',
  'base_id', 'environment', 'base_generation', 'toolset_binding',
  'customer_scope', 'record_type_scopes', 'tool_kinds',
]);

/**
 * The tool keys `summarizeToolsetSurface` classifies by name. A SQL Action (`sql_actions`, #5311)
 * reaches records like an Action: its query reads only the record types it declares, which the
 * resolve carries, so the customer-facing rule judges them as it judges an Action's.
 */
const RESOLVE_TOOL_KEYS: ReadonlySet<string> = new Set([
  'actions', 'composites', 'relationships', 'files', 'batch', 'sql_query', 'sql_actions',
]);

/** Whether a resolve value exposes anything: a non-empty list, `true`, or any other non-empty value. */
function exposesSomething(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (value === null || value === undefined || value === false || value === '') return false;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

/**
 * Reduce a resolved toolset to what the connection records of it (`toolsetSurfaceSchema`): the
 * facts the customer-facing rule reads, whether its calls must name a customer, and the
 * toolset's file tools, which the `data/` mount reads. A key the resolve carries that this side
 * neither describes nor classifies counts as reaching records when it exposes anything, and is
 * recorded as such (`unclassified_tools`): a kind of tool the data product adds later is refused to a
 * customer-facing agent and a customer page until it is classified here, rather than admitted unread.
 * `toolsetId` is the toolset the resolve is of, which a reviewed type's writers name.
 */
export function summarizeToolsetSurface(data: RekorResolvedToolset, toolsetId: string): ToolsetSurface {
  const unclassified = Object.entries(data).some(
    ([key, value]) => !RESOLVE_DESCRIPTIVE_KEYS.has(key) && !RESOLVE_TOOL_KEYS.has(key) && exposesSomething(value),
  );
  const { files, writes } = summarizeFileTools(data.files);
  const records = exposesSomething(data.actions) || exposesSomething(data.composites) || exposesSomething(data.relationships)
    || exposesSomething(data.sql_actions) || unclassified;
  return {
    records,
    sql_query: data.sql_query === true,
    batch: data.batch?.enabled === true,
    customer_scoped: data.customer_scope === true,
    unclassified_tools: unclassified,
    file_writes: writes,
    ...summarizeReview(data, toolsetId, records),
    ...(files.length > 0 ? { files } : {}),
  };
}

/**
 * What a toolset tool does (wayai#5333 decision 6), as the toolset resolve answers it in `tool_kinds`:
 * `read` changes nothing; `write`, `delete` (it can remove data) and `composite` (several writes in one
 * atomic call) all write. `delete` and `composite` are told apart from `write` so a host can confirm them.
 * The data product's own copy is `TOOL_KINDS`; the connector contract's seam guard pins the two as sets.
 */
export const REKOR_TOOL_KINDS = ['read', 'write', 'delete', 'composite'] as const;

export type RekorToolKind = (typeof REKOR_TOOL_KINDS)[number];

/**
 * The query parameter a caller of the toolset resolve sets to `true` to be answered `tool_kinds`. The data
 * product's own spelling is `TOOL_KINDS_PARAM`; the connector contract's seam guard pins the two.
 */
export const REKOR_TOOL_KINDS_PARAM = 'tool_kinds';

/** The resolve's query string, without its `?`, that asks for the tool kinds. */
export const REKOR_TOOL_KINDS_RESOLVE_SEARCH = new URLSearchParams({ [REKOR_TOOL_KINDS_PARAM]: 'true' }).toString();

/** A toolset tool's kind, by the name the toolset publishes it under. */
export type RekorToolKindLookup = (toolName: string) => RekorToolKind;

/**
 * The kind of each tool, from a resolve's `tool_kinds`. Fails closed: a tool the map does not name, a kind
 * outside `REKOR_TOOL_KINDS`, and every tool of a resolve that carries no map (one from a data product that
 * predates it, or answered to a caller that did not ask) read as a `write`.
 */
export function rekorToolKindLookup(toolKinds: Readonly<Record<string, string>> | undefined): RekorToolKindLookup {
  return (toolName) => {
    const kind = toolKinds !== undefined && Object.hasOwn(toolKinds, toolName) ? toolKinds[toolName] : undefined;
    return (REKOR_TOOL_KINDS as readonly unknown[]).includes(kind) ? (kind as RekorToolKind) : 'write';
  };
}

/**
 * The record-type schema key a reviewed record type declares (#5083 RK4): `{ field, audience?, writers?,
 * stamps? }`. The data product's own spelling is `REVIEW_KEY`; the connector contract's seam guard pins the two.
 */
export const REVIEW_DECLARATION_KEY = 'x-review';

/** Whether a record-type schema declares `x-review` at all, well-formed or not. */
function declaresReview(schema: unknown): boolean {
  return typeof schema === 'object' && schema !== null && (schema as Record<string, unknown>)[REVIEW_DECLARATION_KEY] !== undefined;
}

/** The parts of a resolve's record type entry, review declaration and scope this side reads (RK4). */
const resolvedRecordTypeSchema = z.object({ json_schema: z.record(z.unknown()) });
const reviewWritersSchema = z.object({ writers: z.array(z.string()).optional() });
const sharedScopeSchema = z.object({ role: z.literal('shared') });

/**
 * How the toolset reads one record type the resolve expands (`record_types[id]`), from its `x-review`:
 * `writer` when its writers name the toolset, `reader` when it declares a review that names the toolset
 * nowhere, `open` when it declares none, and `unread` when this side cannot read the entry or the
 * declaration, which then counts as writing.
 */
function reviewModeOf(entry: unknown, toolsetId: string): 'writer' | 'reader' | 'open' | 'unread' {
  const parsed = resolvedRecordTypeSchema.safeParse(entry);
  if (!parsed.success) return 'unread';
  const schema = parsed.data.json_schema;
  if (!declaresReview(schema)) return 'open';
  const review = reviewWritersSchema.safeParse(schema[REVIEW_DECLARATION_KEY]);
  if (!review.success) return 'unread';
  return (review.data.writers ?? []).includes(toolsetId) ? 'writer' : 'reader';
}

/**
 * The surface's review facts (#5083 RK4): whether the toolset writes a reviewed record type it reaches
 * (`review_writes`), and whether it only reads approved records of reviewed `shared` types
 * (`approved_reads_only`, decision 182's Q9), from the declarations `record_types` carries, which
 * include every reviewed type it reaches (decision 198). Both fail closed: a resolve reaching records
 * without its record types counts as writing, and a type it reaches whose declaration the resolve does
 * not carry (an unreviewed one only a relationship reaches) is not one it only reads. A toolset with a
 * SQL Action (#5311) never only reads approved records: its query is no review-aware read, so it reads
 * drafts too (the data product refuses one over a reviewed type to any toolset but the type's writers).
 */
function summarizeReview(
  data: RekorResolvedToolset,
  toolsetId: string,
  records: boolean,
): Pick<ToolsetSurface, 'review_writes' | 'approved_reads_only'> {
  const types = data.record_types;
  if (!types) return { review_writes: records, approved_reads_only: false };
  const sqlActions = exposesSomething(data.sql_actions);
  const modes = new Map(Object.entries(types).map(([id, entry]) => [id, reviewModeOf(entry, toolsetId)]));
  const scopes = data.record_type_scopes;
  const reached = scopes ? new Set([...modes.keys(), ...Object.keys(scopes)]) : new Set<string>();
  return {
    review_writes: [...modes.values()].some((mode) => mode === 'writer' || mode === 'unread'),
    approved_reads_only: !sqlActions && reached.size > 0 && [...reached].every((id) => {
      const scope = scopes?.[id];
      return modes.get(id) === 'reader' && sharedScopeSchema.safeParse(scope).success;
    }),
  };
}

/** The file operations that only read; every other one writes. */
const FILE_READ_OPERATIONS: ReadonlySet<unknown> = new Set<ToolsetFileOperation>(['list', 'get', 'get_url']);

/**
 * The part of a resolve's file tool entry this side reads: `{ file_type, path_prefix?, operations,
 * name?, names?, description_override? }`, one per file type and folder, as the toolset declares it
 * (`skills/wayai/references/bases/toolsets.md`, "File tools").
 *
 * The data product checks only the file type, the operations and the folder, and stores every other
 * key as written (a `null` included). So the optional keys are read as they come, and a value this
 * side cannot use loses only itself, never the entry: a dropped entry would leave its file type
 * unmounted, or a write tool unseen beside a mounted file type.
 */
const resolvedFileToolSchema = z.object({
  file_type: z.string().min(1),
  path_prefix: z.string().nullish(),
  operations: z.array(z.unknown()),
  name: z.unknown().optional(),
  names: z.unknown().optional(),
  description_override: z.unknown().optional(),
});

/**
 * The name the data product publishes a file tool's operation under: the entry's own name for that
 * operation when it states one, else `<operation>_<name>`, `name` defaulting to the file type
 * (`list_policies`, `get_policies`). It mirrors the data product's own expression,
 * `names?.[op] ?? \`${op}_${name ?? file_type}\`` (`fileToolName` in its shared toolset code, which
 * this side cannot import), for whatever values the entry holds. A name derived differently from it
 * matches none of the connection's catalogued tools, so the data mount then reaches nothing through
 * that entry: it never reaches a tool the agent was not given.
 */
export function toolsetFileToolName(
  entry: { file_type: string; name?: unknown; names?: unknown },
  op: ToolsetFileOperation,
): string {
  const names = entry.names as Record<string, unknown> | null | undefined;
  const stated = names?.[op];
  return stated !== undefined && stated !== null ? String(stated) : `${op}_${entry.name ?? entry.file_type}`;
}

/**
 * The names of the signed file URL tools a surface records (`TOOLSET_FILE_URL_OPERATIONS`): the tools a
 * toolset connection's catalog leaves out (`fetchToolsetCatalog`) and its toolset call refuses by name.
 * An entry the surface could not
 * read is not recorded, so its URL tools are not named here.
 */
export function toolsetUrlToolNames(surface: Pick<ToolsetSurface, 'files'>): Set<string> {
  const names = new Set<string>();
  for (const entry of surface.files ?? []) {
    for (const op of TOOLSET_FILE_URL_OPERATIONS) {
      const name = entry.tools[op];
      if (name !== undefined) names.add(name);
    }
  }
  return names;
}

/**
 * A resolve's file tools as the connection records them, an entry this side cannot read left out,
 * and whether any of them writes (`file_writes`): an operation but `list`, `get` and `get_url`, or an entry
 * this side cannot read. Both fail closed, so a write the data product adds later is refused to a
 * customer-facing agent until it is classified here, rather than admitted unread.
 */
function summarizeFileTools(files: unknown[] | undefined): { files: ToolsetFileTool[]; writes: boolean } {
  const out: ToolsetFileTool[] = [];
  let writes = false;
  for (const raw of files ?? []) {
    const parsed = resolvedFileToolSchema.safeParse(raw);
    if (!parsed.success) {
      writes = true;
      continue;
    }
    const entry = parsed.data;
    if (entry.operations.some((op) => !FILE_READ_OPERATIONS.has(op))) writes = true;
    const tools: ToolsetFileTool['tools'] = {};
    for (const op of TOOLSET_FILE_OPERATIONS) {
      if (entry.operations.includes(op)) tools[op] = toolsetFileToolName(entry, op);
    }
    if (Object.keys(tools).length === 0) continue;
    const description = typeof entry.description_override === 'string' ? entry.description_override.trim() : '';
    out.push({ file_type: entry.file_type, path_prefix: entry.path_prefix ?? '', ...(description ? { description } : {}), tools });
  }
  return { files: out, writes };
}

// =============================================
// A file tool's answer
// =============================================

/**
 * One file as a file tool answers it. This side reads its full path within its file type and its
 * version (the one a version-checked write names), and keeps every other field the row carries
 * (content type, size, metadata, who changed it and when): the `data/` mount hands an agent the
 * whole row, as the tool it stands in for did. A `list` answers `{ data: [file, …] }`, never content.
 */
export const rekorToolsetFileSchema = z.object({
  path: z.string().min(1),
  version: z.number().int().min(1),
  content_type: z.string().optional(),
}).passthrough();

export type RekorToolsetFile = z.infer<typeof rekorToolsetFileSchema>;

/**
 * What this side reads of a signed file URL tool's answer (`get_url`, `put_url`; #5083 RK6 part 2): the
 * URL, its expiry, and the file it is for. Read only to REFUSE it (owner decision 195, Q8 (A)): a toolset
 * call whose catalogued tool has come to mean a URL tool on the far side (`answersSignedFileUrl`) is
 * answered as an error instead.
 */
export const rekorToolsetFileUrlAnswerSchema = z.object({
  data: z.object({
    url: z.string(),
    expires_at: z.string(),
    path: z.string(),
    content_sha256: z.string(),
  }).passthrough(),
});

/**
 * A signed file URL's token, as the data product mints it (`{version}.{kind}.{exp}.{nonce}.{mac}`, kind `g`
 * or `p`, nonce and MAC 43 base64url characters each; its `lib/file-url-token.ts`). Unreserved in a URL,
 * so it reads the same percent-encoded.
 */
const SIGNED_FILE_URL_TOKEN = /v[0-9]+\.[gp]\.[0-9]+\.[A-Za-z0-9_-]{43}\.[A-Za-z0-9_-]{43}/;

/**
 * Whether a tool result carries a signed file URL's token anywhere in it: what the tool dispatcher refuses
 * in the result of a call it runs (owner decision 195, Q8 (A); `handleTool`).
 */
export function carriesSignedFileUrl(result: unknown): boolean {
  let text: string | undefined;
  try {
    text = typeof result === 'string' ? result : JSON.stringify(result);
  } catch {
    return false;
  }
  return text !== undefined && SIGNED_FILE_URL_TOKEN.test(text);
}

/** Whether a toolset tool's answer carries a signed file URL (`rekorToolsetFileUrlAnswerSchema`). */
export function answersSignedFileUrl(body: unknown): boolean {
  return rekorToolsetFileUrlAnswerSchema.safeParse(body).success;
}

/** A file `list` tool's answer: one page of files. */
export const rekorToolsetFileListSchema = z.object({ data: z.array(rekorToolsetFileSchema) });

/**
 * A file `get` tool's answer: the file with its whole content, as UTF-8 text when it is text and
 * decodes, else base64 (`encoding` says which).
 */
export const rekorToolsetFileGetSchema = z.object({
  data: rekorToolsetFileSchema.extend({
    content: z.string(),
    encoding: z.enum(['utf-8', 'base64']),
  }),
});

/** The file a `get` tool answers, its content included. */
export type RekorToolsetFileGet = z.infer<typeof rekorToolsetFileGetSchema>['data'];

/**
 * A file `put` tool's answer: the file as the write left it, never its content. Its version is the
 * one the file's next version-checked write names. A `put` that names no version only creates, and
 * one that names a version replaces the file only while that is still its current version; either
 * is otherwise refused without a write (the data product's compare-and-set).
 */
export const rekorToolsetFilePutSchema = z.object({ data: rekorToolsetFileSchema });

/**
 * Whether the resolving token is bound to exactly this toolset, on the base it resolved, in this
 * org. The org is what makes the recorded base the one a call attested for the hub's org reaches:
 * another org can hold a base and toolset of the same ids, so a binding whose org the token does
 * not report is not admitted.
 */
export function resolvedToolsetIsBound(data: RekorResolvedToolset, slug: string, orgId: string): boolean {
  const binding = data.toolset_binding;
  return !!binding
    && binding.org_id === orgId
    && binding.base_id === data.base_id
    && binding.toolset_id === slug;
}

// =============================================
// The customer-facing rule (#5083 decisions 12, 13 and 23)
// =============================================

/** Whether `role` is customer-facing (`CUSTOMER_FACING_AGENT_ROLES`, `constants.ts`). */
export { isCustomerFacingAgentRole };

/**
 * Who the customer-facing rule judges a binding for: a customer-facing agent (`CUSTOMER_FACING_AGENT_ROLES`)
 * or a customer page (#5333 decision 7). Every caller of the rule names one: the two are held to different halves.
 */
export type CustomerFacingHolder = 'agent' | 'page';

/**
 * May a customer-facing holder use a connection with this binding? Its tool half binds both (#5083
 * decision 23 and RK4, #5473 §4): never `sql_query` or `batch`, which reach any record; no file tool that
 * writes (premise 8: customer-facing holders are read-only on documents); no writes of a reviewed record
 * type, whose writers read its drafts (RK4: they read approved records only); and no kind of tool this side
 * does not classify.
 *
 * Its record half binds a customer page only (customer pages move onto person rules in #5473 2b2): its
 * record, composite, relationship and SQL Action tools only under a customer scope, where each call reaches
 * only its customer's records and shared ones, or unscoped when they only read approved records of reviewed
 * `shared` types (decision 182, Q9). A customer-facing agent's records are the data product's to confine: it
 * holds every customer-facing call to the audience gate at call time (#5473 §4, 2a: only person-ruled and
 * `{ shared: true }` record types), so the agent's toolset need not be customer-scoped (#5473 2b1).
 *
 * A connection with no binding holds no token and reaches nothing, so it is admitted.
 */
export function toolsetBindingAllowsCustomerFacing(
  binding: ToolsetConnectionBinding | null | undefined,
  holder: CustomerFacingHolder,
): boolean {
  return bindingRefusedToCustomerFacing(binding, holder) === null;
}

/**
 * Why the customer-facing rule refuses a binding for `holder`, or null when it admits it: what the toolset
 * reaches that the rule refuses, or the facts the rule reads that its surface was recorded before
 * (`file_writes`; for a toolset reaching records, `unclassified_tools` and `review_writes`, and for a customer
 * page `customer_scoped`), each of which counts as reaching more until it is recorded.
 */
function customerFacingRefusal(
  binding: ToolsetConnectionBinding | null | undefined,
  holder: CustomerFacingHolder,
): { reaches: string[] } | { unrecorded: string[] } | null {
  if (!binding) return null;
  const { records, sql_query, batch, file_writes, customer_scoped, unclassified_tools, review_writes, approved_reads_only } = binding.surface;
  const page = holder === 'page';
  const reaches = [
    page && records && customer_scoped === false && approved_reads_only !== true ? 'records outside a customer scope' : null,
    records && unclassified_tools === true ? 'a kind of tool WayAI does not classify' : null,
    sql_query ? 'sql_query' : null,
    batch ? 'batch' : null,
    file_writes === true ? 'a file put, delete or put_url' : null,
    review_writes === true ? 'writes of a reviewed record type' : null,
  ].filter((what): what is string => what !== null);
  if (reaches.length > 0) return { reaches };
  const unrecorded = [
    file_writes === undefined ? 'whether its file tools write' : null,
    page && records && customer_scoped === undefined ? 'whether it is customer-scoped' : null,
    records && unclassified_tools === undefined ? 'whether it reaches a kind of tool WayAI does not classify' : null,
    records && review_writes === undefined ? 'whether it writes a reviewed record type' : null,
  ].filter((fact): fact is string => fact !== null);
  return unrecorded.length > 0 ? { unrecorded } : null;
}

/**
 * The binding when the customer-facing rule refuses it for `holder` — what a customer-facing agent, or a
 * customer page, may not use — else null.
 */
export function bindingRefusedToCustomerFacing(
  binding: ToolsetConnectionBinding | null | undefined,
  holder: CustomerFacingHolder,
): ToolsetConnectionBinding | null {
  return binding && customerFacingRefusal(binding, holder) ? binding : null;
}

/**
 * The binding when the customer-facing rule refuses it for `holder` by what its surface records it reaching,
 * else null: never for a fact the surface was recorded before (`file_writes`, `unclassified_tools`,
 * `review_writes`, `customer_scoped`), which `bindingRefusedToCustomerFacing` also counts. A customer-facing
 * agent's call is held to this (#5473 2b1): the config writes hold every binding a customer-facing agent comes
 * to hold to the whole rule, and a binding attached before a fact was recorded keeps being served, as before
 * 2b1, with the data product refusing a customer-facing call's file writes and reviewed writes itself.
 */
export function bindingReachRefusedToCustomerFacing(
  binding: ToolsetConnectionBinding | null | undefined,
  holder: CustomerFacingHolder,
): ToolsetConnectionBinding | null {
  const refusal = binding ? customerFacingRefusal(binding, holder) : null;
  return binding && refusal && 'reaches' in refusal ? binding : null;
}

/**
 * Why the customer-facing rule refuses a binding for `holder`, for its refusal: what it reaches that the
 * rule refuses, or the facts it has no record of yet, with what records them. A tool sync records them on a
 * preview hub; a production hub can't sync, so there only saving its token again does.
 */
export function customerFacingRefusalText(binding: ToolsetConnectionBinding, holder: CustomerFacingHolder): string {
  const refusal = customerFacingRefusal(binding, holder);
  if (!refusal) return holder === 'page' ? 'is one a customer page may use' : 'is one a customer-facing agent may use';
  if ('reaches' in refusal) return `reaches ${refusal.reaches.join(', ')}`;
  const facts = refusal.unrecorded.join(', or ');
  return binding.environment === 'production'
    ? `has no record of ${facts}: save its token again (wayai set-connection-credential) to record it`
    : `has no record of ${facts}: sync the connection's tools, or save its token again, to record it`;
}

/**
 * The hosts the data product's MCP server answers on (`apps/backend/wrangler.toml`, which serves it
 * embedded): its pinned hostname, the zone held until its retirement, and its dev host. Every path
 * on them reaches the data product — a toolset's `/t/{slug}/mcp`, and the admin tools' `/mcp` for
 * any base token. Also the workers.dev hosts of its retired Worker (`rekor-mcp-server`, and
 * `-dev`), which answer nothing once that Worker is deleted: kept so a connection still stored on
 * one is recognised as a data MCP connection.
 */
export const DATA_MCP_HOSTS: readonly string[] = [
  'data-mcp.wayai.pro',
  'mcp.rekor.pro',
  'data-mcp.wayai-dev.com',
  'rekor-mcp-server.wayai.workers.dev',
  'rekor-mcp-server-dev.wayai.workers.dev',
];

/** Whether `url` names a Data MCP host (`DATA_MCP_HOSTS`), on any scheme, port or path; false for one that does not parse. */
export function isDataMcpUrl(url: unknown): boolean {
  if (typeof url !== 'string') return false;
  let hostname: string;
  try {
    hostname = new URL(url.trim()).hostname.toLowerCase().replace(/\.$/, '');
  } catch {
    return false;
  }
  return DATA_MCP_HOSTS.includes(hostname);
}

// =============================================
// The HubDO chokepoint's refusals
// =============================================

/**
 * What an ingress matches `TOOLSET_CUSTOMER_FACING_REFUSAL` by: its wording before premise 8's file
 * writes and decision 23's customer scope joined the rule, which the refusal still begins with. A
 * deploy runs old and new code side by side, so an ingress of either version relays the refusal
 * whichever version's HubDO threw it.
 */
const TOOLSET_CUSTOMER_FACING_REFUSAL_MARK =
  'A customer-facing agent (a pilot, pilot specialist or pilot advisor) can use a toolset only when it exposes files alone: no record, composite or relationship tools, and neither sql_query nor batch';

/**
 * A customer-facing agent, or a customer page, and a toolset the customer-facing rule refuses it, meeting on one
 * write. It still begins with `TOOLSET_CUSTOMER_FACING_REFUSAL_MARK`, which the record half no longer bounds for an
 * agent (#5473 2b1): the sentence after the mark states the rule as it stands.
 */
export const TOOLSET_CUSTOMER_FACING_REFUSAL = `${TOOLSET_CUSTOMER_FACING_REFUSAL_MARK}, nor a file put, delete or put_url, `
  + 'nor a write of a reviewed record type, nor a kind of tool WayAI does not classify. Its record, composite, '
  + 'relationship and SQL Action tools are admitted to a customer-facing agent, since the data product holds each of its '
  + 'calls to the record types that declare person rules or shared access; to a customer page only when the toolset is '
  + 'customer-scoped, or they only read approved records of reviewed shared record types';

/**
 * A customer-facing agent and a connection other than a Base Toolset one whose base URL is a Data MCP
 * host (`isDataMcpUrl`), meeting on one write (wayai-pro/wayai#5332). Such a connection records no
 * binding, so the customer-facing rule could never judge what it reaches: a customer-facing agent
 * reaches the data product only through a Base Toolset connection.
 */
export const GENERIC_DATA_MCP_CUSTOMER_FACING_REFUSAL =
  'A customer-facing agent (a pilot, pilot specialist or pilot advisor) can reach a data toolset only through a Base Toolset connection, '
  + 'never through a generic MCP connection to the data MCP server';

/**
 * A toolset connection write prepared over another binding than the one now stored: a tool sync
 * recorded a new surface, or a token save a new binding, since the writer read it. Writing would
 * revert that fresher record, so the write is refused (`HubDO.updateConnection`'s
 * `expectedToolsetBinding`); the writer reads the connection again.
 */
export const TOOLSET_CONNECTION_CHANGED_REFUSAL =
  'This toolset connection changed while this write was being prepared: reload it and try again';

/**
 * A tool on a toolset connection that is not one of its toolset's catalogued tools: an
 * `mcp_tool_call` assignment naming a row of the connection's own `mcp_tool` catalog (written only by its
 * tool sync, or copied from the other branch's) under that row's tool name. Every tool on the connection reaches the toolset, so
 * any other would reach a live toolset tool outside the catalog and the surface the customer-facing
 * rule reads. Judged at every tool write, and again at each call.
 */
export const TOOLSET_CATALOG_ONLY_REFUSAL =
  "A toolset connection's tools are its toolset's own: sync the connection, then give the agent one of the tools it catalogs";

/** A toolset connection recording a base of the other environment. */
export const TOOLSET_ENVIRONMENT_REFUSAL =
  "A toolset connection can hold only a token for a base in its hub's own environment";

/**
 * A toolset connection's token written without the binding resolved from it, or a binding left
 * behind with no token. Only a write path that skipped the connection-write hook can produce it,
 * so it is a fault, never the author's 4xx: `toolsetConnectionRefusalMessage` does not match it.
 */
export const TOOLSET_UNRESOLVED_CREDENTIAL_REFUSAL =
  "A toolset connection's token can be stored only together with the binding resolved from it";

/**
 * The author-facing refusal `err` carries — its whole message, which names what met — or null.
 * The chokepoint's refusals cross the RPC boundary as plain `Error`s, so an ingress matches them
 * by these sentences and answers a 4xx rather than an internal fault.
 */
export function toolsetConnectionRefusalMessage(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const { message } = err;
  return message.includes(TOOLSET_CUSTOMER_FACING_REFUSAL_MARK) || message.includes(TOOLSET_ENVIRONMENT_REFUSAL)
    || message.includes(TOOLSET_CONNECTION_CHANGED_REFUSAL) || message.includes(GENERIC_DATA_MCP_CUSTOMER_FACING_REFUSAL)
    ? message
    : null;
}
